// Home-screen shortcut helpers. The browser's install prompt is captured and only shown on a user tap.
export type Platform = "ios" | "android" | "desktop";

export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export function detectPlatform(ua: string, maxTouchPoints = 0): Platform {
  if (/iPhone|iPad|iPod/i.test(ua) || (/Macintosh/i.test(ua) && maxTouchPoints > 1)) return "ios";
  if (/Android/i.test(ua)) return "android";
  return "desktop";
}

/** Instagram, Facebook, WhatsApp, TikTok, Snapchat, LINE etc. in-app browsers cannot add home-screen icons. */
export function isInAppBrowser(ua: string): boolean {
  return /Instagram|FBAN|FBAV|FB_IAB|WhatsApp|TikTok|musical_ly|Snapchat|Line\/|LinkedInApp|Twitter/i.test(ua);
}

export function isStandalone(win: Window = window): boolean {
  return !!(win.matchMedia?.("(display-mode: standalone)").matches || (win.navigator as any).standalone === true);
}

let deferred: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();
let installed = false;
const notify = () => listeners.forEach((l) => l());

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault(); // never auto-show; keep for a user tap
    deferred = e as BeforeInstallPromptEvent;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    installed = true;
    deferred = null;
    notify();
  });
}

export const installState = {
  canPrompt: () => !!deferred,
  installed: () => installed,
  subscribe: (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; },
  /** Shows the native prompt. Returns the outcome; "unavailable" if no prompt or it failed. */
  async prompt(): Promise<"accepted" | "dismissed" | "unavailable"> {
    const e = deferred;
    if (!e) return "unavailable";
    deferred = null; // a captured prompt can only be used once
    notify();
    try {
      await e.prompt();
      const { outcome } = await e.userChoice;
      return outcome;
    } catch {
      return "unavailable";
    }
  },
};
